import React, { useState, useEffect, useMemo } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import isoWeek from "dayjs/plugin/isoWeek";
import { getDashboardTrades } from "../services/api";
import DailyTradesChart from "../components/DailyTradesChart";
// import IdleLogout from "../components/IdleLogout";
import { aggregateTrades } from "../utils/aggregateTrades";
import "./DashboardPage.css";
import MarketStatus from "../components/MarketStatus";

dayjs.extend(isoWeek);

const STARTING_AUM = {
  ALL: 38415.39,
  Q_SP_A: 38415.39,
  FUND_2: 38415.39,
};

const funds = [
  { code: "ALL", name: "All Funds" },
  { code: "Q_SP_A", name: "Quant S&P Alpha" },
  { code: "FUND_2", name: "Fund 2" },
];

function getStatus(value, { good, warning }) {
  if (value >= good) return "positive";
  if (value >= warning) return "neutral";
  return "negative";
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const firstName = localStorage.getItem("firstName") || "User";

  const [selectedFund, setSelectedFund] = useState("ALL");
  const [dashboardTrades, setDashboardTrades] = useState([]);
  const [loading, setLoading] = useState(true);

  const [aggregation, setAggregation] = useState("raw");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // -------------------------
  // Fetch raw trades
  // -------------------------
  useEffect(() => {
    const fetchTrades = async () => {
      try {
        // console.log("📤 Fetching trades for fund:", selectedFund);
        const trades = await getDashboardTrades(selectedFund);
        setDashboardTrades(trades);
      } catch (err) {
        console.error("Failed to load trades", err);
        setDashboardTrades([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTrades();
  }, [selectedFund]);

  // -------------------------
  // Date-filtered trades (SINGLE SOURCE OF TRUTH)
  // -------------------------
  const filteredTrades = useMemo(() => {
    if (!startDate && !endDate) return dashboardTrades;

    return dashboardTrades.filter((trade) => {
      const tradeDate = dayjs.utc(trade.trade_date);

      if (startDate && tradeDate.isBefore(dayjs.utc(startDate), "day")) {
        return false;
      }

      if (endDate && tradeDate.isAfter(dayjs.utc(endDate), "day")) {
        return false;
      }

      return true;
    });
  }, [dashboardTrades, startDate, endDate]);

  // -------------------------
  // Dashboard callout stats 🔄 UPDATED
  // -------------------------
  const dashboardStats = useMemo(() => {
    const totalTrades = filteredTrades.length;

    const totalPnL = filteredTrades.reduce(
      (sum, t) => sum + Number(t.cash_return || 0),
      0
    );

    const wins = filteredTrades.filter(
      (t) => Number(t.cash_return) > 0
    ).length;

    const battingAverage = totalTrades
      ? (wins / totalTrades) * 100
      : 0;

    return {
      totalTrades,
      totalPnL,
      battingAverage,
    };
  }, [filteredTrades]);

  const startingAum =
  STARTING_AUM[selectedFund] ?? STARTING_AUM.ALL;
  const currentAum = startingAum + dashboardStats.totalPnL;

  // -------------------------
  // Aggregated table data
  // -------------------------
  const tableData = useMemo(() => {
    return aggregateTrades(filteredTrades, aggregation);
  }, [filteredTrades, aggregation]);
  
  // -------------------------
  // Gain : Loss Ratio 🔄 UPDATED
  // -------------------------
  const overallGainLossRatio = useMemo(() => {
  if (!tableData || tableData.length === 0) return 0;

  const winningPeriods = tableData.filter(r => r.pnl > 0);
  const losingPeriods = tableData.filter(r => r.pnl < 0);

  if (!winningPeriods.length || !losingPeriods.length) return 0;

  const avgWin =
    winningPeriods.reduce((sum, r) => sum + r.pnl, 0) /
    winningPeriods.length;

  const avgLoss =
    Math.abs(
      losingPeriods.reduce((sum, r) => sum + r.pnl, 0) /
      losingPeriods.length
    );

  return avgLoss > 0 ? avgWin / avgLoss : 0;
  }, [tableData]);

  // -------------------------
  // Table totals
  // -------------------------
  const tableTotals = useMemo(() => {
    if (!tableData.length) return null;

    return tableData.reduce(
      (acc, row) => {
        acc.trades += row.trades || 0;
        acc.pnl += row.pnl || 0;
        acc.wins += row.wins || 0;
        return acc;
      },
      { trades: 0, pnl: 0, wins: 0 }
    );
  }, [tableData]);

  // -------------------------
  // Greeting
  // -------------------------
  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 18) return "Good afternoon";
    return "Good evening";
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  if (loading) {
    return (
      <p style={{ textAlign: "center", marginTop: "50px" }}>
        Loading dashboard...
      </p>
    );
  }

  // -------------------------
  // CountUp component
  // -------------------------
  function CountUp({ value, duration = 1000, decimals = 0 }) {
    const [count, setCount] = useState(0);

    useEffect(() => {
      let start = 0;
      const increment = value / (duration / 16);
      const timer = setInterval(() => {
        start += increment;
        if (start >= value) {
          setCount(value);
          clearInterval(timer);
        } else {
          setCount(start);
        }
      }, 16);
      return () => clearInterval(timer);
    }, [value, duration]);

    return (
      <span>
        {Number(count).toLocaleString(undefined, {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals,
        })}
      </span>
    );
  }

  return (
    <div className="dashboard-wrapper">
      <Helmet>
        <title>RCM | Dashboard</title>
      </Helmet>

      <nav className="dashboard-navbar">
        <div className="dashboard-logo" onClick={() => navigate("/")}>
          <a href="/" className="logo">
            <img src="/RCM-Logo-White.png" alt="RCM Logo" />
          </a>
        </div>
        <div className="dashboard-nav-spacer"></div>
        <button className="dashboard-btn" onClick={handleLogout}>
          Logout
        </button>
      </nav>

      <header className="dashboard-header">
        <div className="dashboard-header-flex">
          <h1 className="dashboard-greeting">
            {getGreeting()}
            {firstName ? `, ${firstName}` : ""}
          </h1>
        </div>

        <div className="dashboard-header-row">
          <div className="fund-filter fund-filter-header">
            <label htmlFor="fund-select">Fund:</label>
            <select
              id="fund-select"
              value={selectedFund}
              onChange={(e) => setSelectedFund(e.target.value)}
            >
              {funds.map((f) => (
                <option key={f.code} value={f.code}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>
          <MarketStatus />
        </div>
      </header>

      {/* Callouts 🔄 UPDATED */}
      <section className="callouts">
        <div className={`callout-box ${getStatus(
            // dashboardStats.totalPnL,
            currentAum.toLocaleString(undefined, {maximumFractionDigits: 0,}),
            { good: 46000 }
          )}`}
        >
          <h2>AUM</h2>
          <p>$<CountUp value={currentAum} /></p>
        </div>

        <div
          className={`callout-box ${getStatus(
            dashboardStats.totalPnL,
            { good: 0 }
          )}`}
        >
          <h2>Total Return</h2>
          <p>$<CountUp value={dashboardStats.totalPnL} /></p>
        </div>

        <div className="callout-box neutral">
          <h2>Total Trades</h2>
          <p><CountUp value={dashboardStats.totalTrades} /></p>
        </div>

        <div
          className={`callout-box ${getStatus(
            dashboardStats.battingAverage,
            { good: 51, warning: 50 }
          )}`}
        >
          <h2>Batting Average</h2>
          <p><CountUp value={dashboardStats.battingAverage} decimals={0} />%</p>
        </div>

        <div
          className={`callout-box ${getStatus(
            overallGainLossRatio,
            { good: 1, warning: 0.9 }
          )}`}
        >
          <h2>Gain:Loss Ratio</h2>
          <p><CountUp value={overallGainLossRatio} decimals={2} /></p>
        </div>
      </section>

      {/* Chart 🔄 UPDATED */}
      <section className="charts">
        <div className="chart-container">
          <h2>Fund Performance</h2>
          <div className="table-controls-row">
            {/* View filter */}
            <div className="date-input-group">
              <label htmlFor="aggregation-select">View:</label>
              <select
                id="aggregation-select"
                value={aggregation}
                onChange={(e) => setAggregation(e.target.value)}
              >
                <option value="raw">Raw Trades</option>
                <option value="day">By Day</option>
                <option value="week">By Week</option>
                <option value="month">By Month</option>
                <option value="year">By Year</option>
              </select>
            </div>

            {/* From date */}
            <div className="date-input-group">
              <label htmlFor="start-date">From:</label>
              <input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>

            {/* To date */}
            <div className="date-input-group">
              <label htmlFor="end-date">To:</label>
              <input
                id="end-date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>

            {/* Clear button */}
            {(startDate || endDate) && (
              <div className="date-input-group">
                <label style={{ visibility: 'hidden' }}>Clear:</label>
                <button
                  onClick={() => {
                    setStartDate("");
                    setEndDate("");
                  }}
                >
                  Clear Dates
                </button>
              </div>
            )}
          </div>
          <DailyTradesChart data={filteredTrades} />
        </div>
      </section>

      {/* Table */}
      <section className="trades-table-section">
        <div className="trades-table-wrapper">
          <div className="trades-table-scroll">
            <table className="trades-table">
              <thead>
                <tr>
                  <th>Period</th>
                  <th>Trades</th>
                  <th>PnL</th>
                  <th>Win Rate %</th>
                  {aggregation !== "raw" && <th>G:L Ratio</th>}
                </tr>
              </thead>

              <tbody>
                {tableData.length > 0 ? (
                  tableData.map((row, idx) => (
                    <tr key={idx}>
                      <td>{row.period}</td>
                      <td>{row.trades}</td>
                      <td
                        className={row.pnl >= 0 ? "pnl-positive" : "pnl-negative"}
                      >
                        ${row.pnl.toFixed(2)}
                      </td>
                      <td>{row.winRate.toFixed(2)}%</td>
                      {aggregation !== "raw" && (
                        <td>{row.gainLossRatio.toFixed(2)}</td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={aggregation !== "raw" ? 5 : 4}>
                      No trade data
                    </td>
                  </tr>
                )}
              </tbody>

              {/* TOTAL ROW */}
              {tableTotals && (
                <tfoot>
                  <tr>
                    <td>Total</td>
                    <td>{tableTotals.trades}</td>
                    <td
                      className={
                        tableTotals.pnl >= 0
                          ? "pnl-positive"
                          : "pnl-negative"
                      }
                    >
                      ${tableTotals.pnl.toFixed(2)}
                    </td>
                    <td>
                      {tableTotals.trades
                        ? ((tableTotals.wins / tableTotals.trades) * 100).toFixed(1)
                        : "0.0"}
                      %
                    </td>
                    {aggregation !== "raw" && <td>—</td>}
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </section>

      <footer>
        <p>&copy; 2026 Rebus Capital Management LLC | All rights reserved.</p>
      </footer>
    </div>
  );
}